export const YDOC_BLOCKS = 'blocks';
export const GRAPH_LISTS = ['nodes', 'edges', 'rels', 'groups', 'summaries'];
const ORDER = '_i';
/** Поля, которые не входят в состояние Yjs (пересчитываются или служебные). */
const SKIP = new Set(['schemaVersion', ...GRAPH_LISTS, 'style']);
/** Значение для Y.Map: копия без служебного поля порядка (сравнивается по JSON). */
const clean = (x) => {
    const rest = { ...x };
    delete rest[ORDER];
    return rest;
};
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
/** Заполнить пустой Y.Doc содержимым карты или доски (первое открытие). */
export function graphToY(doc, content) {
    doc.transact(() => {
        for (const k of GRAPH_LISTS) {
            const list = content[k];
            if (!Array.isArray(list))
                continue;
            const m = doc.getMap(k);
            list.forEach((x, i) => m.set(x.id, { ...clean(x), [ORDER]: i }));
        }
        const style = doc.getMap('style');
        for (const [k, v] of Object.entries(content.style ?? {}))
            style.set(k, v);
        const extra = doc.getMap('extra'); // прочие поля верхнего уровня (на будущее), чтобы не терялись
        for (const [k, v] of Object.entries(content))
            if (!SKIP.has(k))
                extra.set(k, v);
    });
}
/** JSON карты или доски из Y.Doc: порядок по `_i`, затем по id. */
export function yToGraph(doc) {
    const out = { schemaVersion: 1 };
    for (const [k, v] of doc.getMap('extra').entries())
        out[k] = v;
    for (const k of GRAPH_LISTS) {
        const m = doc.getMap(k);
        if (!m.size && k !== 'nodes' && k !== 'edges')
            continue;
        out[k] = [...m.values()]
            .sort((a, b) => Number(a[ORDER] ?? 0) - Number(b[ORDER] ?? 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
            .map(clean);
    }
    const style = doc.getMap('style');
    if (style.size)
        out.style = Object.fromEntries(style.entries());
    return out;
}
/**
 * Локальные правки карты (прототип меняет JSON целиком) → минимальные изменения Y.Doc: по id добавить, заменить,
 * удалить; оформление — по ключам. Возвращает true, если что-то изменилось.
 */
export function applyGraph(doc, next, origin = null) {
    let changed = false;
    doc.transact(() => {
        for (const k of GRAPH_LISTS) {
            const list = Array.isArray(next[k]) ? next[k] : null;
            if (!list)
                continue;
            const m = doc.getMap(k);
            const ids = new Set(list.map((x) => x.id));
            for (const id of [...m.keys()])
                if (!ids.has(id)) {
                    m.delete(id);
                    changed = true;
                }
            let maxI = Math.max(-1, ...[...m.values()].map((x) => Number(x[ORDER] ?? -1)));
            list.forEach((x) => {
                const cur = m.get(x.id);
                const body = clean(x);
                if (cur && same(clean(cur), body))
                    return;
                m.set(x.id, { ...body, [ORDER]: cur ? cur[ORDER] : ++maxI });
                changed = true;
            });
        }
        if (next.style) {
            const style = doc.getMap('style');
            for (const k of [...style.keys()])
                if (!(k in next.style)) {
                    style.delete(k);
                    changed = true;
                }
            for (const [k, v] of Object.entries(next.style))
                if (!same(style.get(k), v)) {
                    style.set(k, v);
                    changed = true;
                }
        }
    }, origin);
    return changed;
}
/** Y.Doc пустой — содержимое ещё не перенесено из JSON. */
export const isEmptyYDoc = (doc) => doc.getXmlFragment(YDOC_BLOCKS).length === 0 && GRAPH_LISTS.every((k) => doc.getMap(k).size === 0);
/**
 * Свои правки карты → Y.Doc: только то, что изменил этот клиент (разница prev → next), а не вся карта.
 * Так узел, добавленный другим участником мгновение назад и ещё не показанный здесь, не удалится.
 */
export function applyGraphDiff(doc, prev, next, origin = null) {
    let changed = false;
    doc.transact(() => {
        for (const k of GRAPH_LISTS) {
            const before = new Map((Array.isArray(prev[k]) ? prev[k] : []).map((x) => [x.id, x]));
            const after = Array.isArray(next[k]) ? next[k] : [];
            const m = doc.getMap(k);
            const ids = new Set(after.map((x) => x.id));
            for (const id of before.keys())
                if (!ids.has(id) && m.has(id)) {
                    m.delete(id);
                    changed = true;
                }
            let maxI = Math.max(-1, ...[...m.values()].map((x) => Number(x[ORDER] ?? -1)));
            for (const x of after) {
                const was = before.get(x.id);
                if (was && same(clean(was), clean(x)))
                    continue;
                const cur = m.get(x.id);
                m.set(x.id, { ...clean(x), [ORDER]: cur ? cur[ORDER] : ++maxI });
                changed = true;
            }
        }
        const ps = prev.style ?? {}, ns = next.style ?? {};
        const style = doc.getMap('style');
        for (const k of Object.keys(ps))
            if (!(k in ns)) {
                style.delete(k);
                changed = true;
            }
        for (const [k, v] of Object.entries(ns))
            if (!same(ps[k], v)) {
                style.set(k, v);
                changed = true;
            }
    }, origin);
    return changed;
}
//# sourceMappingURL=collab.js.map